import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { requestsService } from "../services/requestsService";
import { ReactComponent as TrashIcon } from "../assets/icons/trash.svg";

const RECORDING_BAR_HEIGHTS = [...Array(10)].map(() => Math.random() * 20 + 10);

interface VoiceNoteModalProps {
  onClose: () => void;
}

const VoiceNoteModal: React.FC<VoiceNoteModalProps> = (props: VoiceNoteModalProps) => {
  const { onClose } = props;
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [additionalContext, setAdditionalContext] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();

  useEffect(() => {
    // start timer and start listening once user starts recording
    if (isRecording && listening) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    // clear timer and stop listening on component dismount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (listening) {
        SpeechRecognition.stopListening();
      }
    };
  }, [isRecording, listening]);

  const startRecording = () => {
    if (!browserSupportsSpeechRecognition) {
      setSaveError("Speech recognition is not supported in this browser");
      return;
    }

    // set all state variables to their starts / defaults
    setRecordingTime(0);
    setSaveError(null);
    setSaveSuccess(false);
    setIsRecording(true);
    SpeechRecognition.startListening({ continuous: true });
  };

  const stopRecording = () => {
    if (listening) {
      SpeechRecognition.stopListening();
    }
    setIsRecording(false);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
    event.target.value = '';
  };

  const addFiles = (newFiles: File[]) => {
    if (newFiles.length === 0) return;
    
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif'
    ];
    
    const validFiles = newFiles.filter(file => {
      return allowedTypes.includes(file.type) || 
             file.name.toLowerCase().endsWith('.pdf') ||
             file.name.toLowerCase().endsWith('.doc') ||
             file.name.toLowerCase().endsWith('.docx') ||
             file.name.toLowerCase().endsWith('.txt');
    });

    setFiles(prev => {
      const existingFileNames = prev.map(f => f.name);
      const newUniqueFiles = validFiles.filter(file => 
        !existingFileNames.includes(file.name)
      );
      
      if (validFiles.length !== newFiles.length) {
        setSaveError(`Some files were skipped. Only PDF, DOC, DOCX, TXT, and image files are allowed.`);
      } else if (newUniqueFiles.length !== validFiles.length) {
        setSaveError(`Some files were skipped because they're already added.`);
      } else if (saveError) {
        setSaveError(null);
      }
      
      return [...prev, ...newUniqueFiles];
    });
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Combine transcript with additional context
      const fullContext = additionalContext 
        ? `${transcript}\n\nAdditional context: ${additionalContext}` 
        : transcript;

      const createdRequest = await requestsService.createVoiceNote({
        transcription: fullContext,
        duration: recordingTime,
        files: files.length > 0 ? files : undefined,
      });

      setSaveSuccess(true);
      resetTranscript();
      setAdditionalContext("");
      setFiles([]);
      
      // Navigate to the created request after a short delay
      setTimeout(() => {
        onClose();
        navigate(`/requests/view/${createdRequest.request_id}`);
      }, 1500);
    } catch (error: any) {
      console.error("Error saving voice note:", error);
      if (error.response?.status === 422 && error.response?.data?.errors) {
        setSaveError(error.response.data.errors.join('. '));
      } else {
        setSaveError(error instanceof Error ? error.message : "Failed to save voice note");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="voice-modal-overlay">
      <div className="voice-modal-content">
        <div style={{
          padding: "24px 24px 20px 24px",
          borderBottom: "1px solid #e5e7eb",
          position: "relative"
        }}>
          <button
            onClick={() => {
              stopRecording();
              onClose();
            }}
            style={{
              position: "absolute",
              top: "24px",
              right: "24px",
              background: "none",
              border: "none",
              color: "#6b7280",
              fontSize: "14px",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0
            }}
          >
            Cancel
          </button>
          <h2 className="voice-modal-header">Add voice note</h2>
          <p className="voice-modal-description">
            Record a quick voice note and we'll create your next pieces of content using the transcript.
          </p>
        </div>

        <div style={{ padding: "24px" }}>

        {/* Context section - always visible */}
        <div className="voice-modal-context-section">
          <label htmlFor="additionalContext" className="voice-modal-context-label">
            <strong>Additional Context (Optional):</strong>
          </label>
          <textarea
            id="additionalContext"
            value={additionalContext}
            onChange={(e) => setAdditionalContext(e.target.value)}
            placeholder="Add any additional context or notes about this voice note..."
            className="voice-modal-context-textarea"
            rows={3}
          />
        </div>

        {/* File Upload Section */}
        <div 
          className="voice-modal-context-section"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            marginBottom: "8px"
          }}>
            <label className="voice-modal-context-label">
              <strong>Upload Files (Optional):</strong>
            </label>
            <label className="tertiary-info-button" style={{ cursor: "pointer", fontSize: "12px", padding: "4px 8px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: "4px" }}>
                <path d="M12 2L12 14M12 2L8 6M12 2L16 6M6 14L6 20C6 21.1046 6.89543 22 8 22L16 22C17.1046 22 18 21.1046 18 20L18 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Choose Files
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                style={{ display: "none" }}
                accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
              />
            </label>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div style={{
              border: `1px solid ${isDragOver ? "#6366f1" : "#e5e7eb"}`,
              borderRadius: "6px",
              backgroundColor: isDragOver ? "#f0f9ff" : "#f9fafb",
              transition: "all 0.2s ease",
              marginBottom: "8px"
            }}>
              {files.map((file, index) => (
                <div 
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 12px",
                    borderBottom: index < files.length - 1 ? "1px solid #e5e7eb" : "none"
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontSize: "12px", 
                      fontWeight: 500, 
                      color: "#374151",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}>
                      {file.name}
                    </div>
                    <div style={{ 
                      fontSize: "10px", 
                      color: "#6b7280",
                      marginTop: "1px"
                    }}>
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    style={{
                      padding: "4px",
                      backgroundColor: "transparent",
                      border: "1px solid #e5e7eb",
                      borderRadius: "3px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.15s",
                      marginLeft: "8px"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#fee2e2";
                      e.currentTarget.style.borderColor = "#fca5a5";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.borderColor = "#e5e7eb";
                    }}
                  >
                    <TrashIcon style={{ width: "12px", height: "12px", color: "#ef4444" }} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {files.length === 0 && (
            <div style={{
              border: `2px dashed ${isDragOver ? "#6366f1" : "#d1d5db"}`,
              borderRadius: "6px",
              padding: "16px",
              textAlign: "center",
              color: isDragOver ? "#6366f1" : "#6b7280",
              fontSize: "12px",
              backgroundColor: isDragOver ? "#f0f9ff" : "transparent",
              transition: "all 0.2s ease"
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: "0 auto 6px", display: "block", color: isDragOver ? "#6366f1" : "#9ca3af" }}>
                <path d="M12 2L12 14M12 2L8 6M12 2L16 6M6 14L6 20C6 21.1046 6.89543 22 8 22L16 22C17.1046 22 18 21.1046 18 20L18 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {isDragOver 
                ? "Drop files here to upload" 
                : "Drag files here or click \"Choose Files\" to upload"
              }
            </div>
          )}
        </div>

        <div className="voice-modal-recording-area">
          {!isRecording && !transcript && (
            <div>
              <button className="voice-modal-button" onClick={startRecording}>
                Start recording
              </button>
            </div>
          )}

          {isRecording && (
            <div>
              <div className="voice-modal-recording-box">
                <div className="voice-modal-waveform">
                  {RECORDING_BAR_HEIGHTS.map((height, i) => (
                    <div
                      key={i}
                      className="voice-modal-wave-bar"
                      style={{
                        height: `${height}px`,
                        animationDelay: `${i * 0.1}s`
                      }}
                    />
                  ))}
                </div>
                <div className="voice-modal-timer">{formatTime(recordingTime)}</div>
              </div>

              {transcript && (
                <div className="voice-modal-transcription">
                  <strong>Transcription:</strong> {transcript}
                </div>
              )}

              <button className="voice-modal-button" onClick={stopRecording}>
                Stop Recording
              </button>
            </div>
          )}

          {transcript && !isRecording && (
            <div>
              <div className="voice-modal-recording-box">
                <div className="voice-modal-final-transcription">
                  <strong>Final Transcription:</strong>
                  <p>{transcript}</p>
                </div>
                <div className="voice-modal-duration-text">
                  Duration: {formatTime(recordingTime)}
                </div>
              </div>

              <button
                className="voice-modal-button"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Creating Note...' : 'Save Note'}
              </button>
            </div>
          )}

          {saveError && (
            <div className="voice-modal-error">
              {saveError}
            </div>
          )}

          {saveSuccess && (
            <div className="voice-modal-success">
              Voice note saved successfully!
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceNoteModal;
